import { Instance, types, flow } from 'mobx-state-tree';
import { MeoCartItem } from './MeoCartItem';
import { enrollmentResource } from '../../../infrastructure/http/EnrollmentResource';
import { getRootStore } from '../../root/RootStoreUtils';
import { WarningReasonType } from '../er-kit-selection/MeoWarning';

export const MeoCartItemSummaryStore = types
  .model('MeoCartItemSummaryStore', {
    meoCartItems: types.array(MeoCartItem),
    orderRequiredModal: types.optional(types.boolean, false),
    enrollInErButtonValue: types.maybe(types.boolean)
  })
  .views(self => ({
    canProceed(): boolean {
      const rootStore = getRootStore(self);
      const kitStore = rootStore.moduleStores.enrollmentKitSelection! ?? rootStore.moduleStores.socialLinkEntry!;
      //In CEO markets the only requirements for enrollment order is PV amount
      //PH market asked to allow to bypass the MEO limit by choosing starter kit
      const canProceed =
        this.getOrderTotalPV() >= kitStore.minimumEnrollmentOrderPv ||
        (kitStore.allowToBypassMeoRequirementWithPSK && kitStore.selectedKit);
      return !!canProceed;
    },
    getOrderTotalPV(): number {
      return self.meoCartItems.reduce((accumulator, item) => {
        return accumulator + item.itemPV * item.quantity;
      }, 0);
    },
    getWarningType(): WarningReasonType {
      const rootStore = getRootStore(self);
      const erKitStore = rootStore.moduleStores.erKitSelection ?? rootStore.moduleStores.socialLinkErKitSelection!;

      //return a warning for any order required if nothing is selected
      if (!rootStore.moduleStores.enrollmentKitSelection?.selectedKit && this.getOrderTotalPV() === 0 && rootStore.enrollmentStatus.countryIso !=="JP") {
        return WarningReasonType.AnyOrderRequired;
      }
      if ((rootStore.moduleStores.socialLinkErKitSelection && erKitStore.enrollEssentialRewards) && (rootStore.moduleStores.enrollmentKitSelection?.selectedKit && !rootStore.moduleStores.enrollmentKitSelection?.selectedKit?.isAutoShipAvailable)) {
        return WarningReasonType.ERPskAlert;
      }
      //otherwise return a warning based on the answer on a ER question
      if (erKitStore.enrollEssentialRewards ?? self.enrollInErButtonValue) {
        return WarningReasonType.EROrderUnderPV;
      } else {
        return WarningReasonType.EnrollmentOrderUnderPV;
      }
    }
  }))
  .actions(self => ({
    toggleWarningMessage(pressedValue?: boolean) {
      //Preserving the ER button value to determine which MEO warning show
      self.enrollInErButtonValue = pressedValue;
      if (!self.canProceed()) {
        //Reseting the ER question values and showing warning message
        const rootStore = getRootStore(self);
        const erKitStore = rootStore.moduleStores.erKitSelection ?? rootStore.moduleStores.socialLinkErKitSelection!;
        erKitStore?.updateEssentialRewards(undefined);
        if (erKitStore?.useErSingleQuestion) {
          erKitStore?.updateUsePremiumStarterKit(undefined);
        }
        self.orderRequiredModal = true;
        return;
      } else {
        self.orderRequiredModal = false;
      }
    }
  }))
  .actions(self => ({
    refreshMeoCart: flow(function* refreshMeoCart(selectedKit): any {
      const posturl = selectedKit === undefined ? `/update-meo-cart` : `/update-meo-cart/${selectedKit.itemId}`;
      const response = yield enrollmentResource.put(posturl);
      self.meoCartItems = response.data;
      self.toggleWarningMessage(self.enrollInErButtonValue);
    })
  }));

export type MeoCartItemSummaryStore = Instance<typeof MeoCartItemSummaryStore>;
